// Define FileMetadata locally since it's not exported from api
export interface FileMetadata {
  filename: string;
  size: number;
  content_type: string;
  created_at: string;
  updated_at: string;
}

export type MetaValidationMode = 'server' | 'client';

export interface ServerValidationResult {
  isAllowed: boolean;
  exists: boolean;
  error?: string;
}

export type FileUploadStrategy = 'skip' | 'overwrite';

export interface FileValidationStatus {
  id: string;
  status:
    | 'pending'
    | 'validating'
    | 'valid'
    | 'invalid'
    | 'error'
    | 'server-validating'
    | 'server-valid'
    | 'server-invalid'
    | 'requires-strategy';
  message?: string;
  errors?: string[];
  warnings?: string[];
  validatedAt?: Date;
  serverValidation?: {
    isAllowed: boolean;
    exists: boolean;
    error?: string;
  };
}

export interface FileWithValidation {
  file: File;
  validationStatus: FileValidationStatus;
  webkitRelativePath?: string;
  isDuplicate?: boolean;
  duplicateMessage?: string;
  uploadStrategy?: FileUploadStrategy;
}

export interface MetaValidationProgress {
  isRunning: boolean;
  isPaused?: boolean;
  totalFiles: number;
  completedFiles: number;
  remainingFiles: number;
  percent: number;
  allowedCount?: number;
  notAllowedCount?: number;
  existsCount?: number;
  totalBatches: number;
  completedBatches: number;
  failedBatches: number;
  runningBatches: number;
  batches: Array<{
    id: string;
    index: number; // 0-based
    fromFileIndex: number; // 0-based (for display/debug)
    toFileIndex: number; // exclusive
    fileCount: number;
    status: 'pending' | 'processing' | 'completed' | 'error' | 'cancelled';
    error?: string;
  }>;
  config: {
    batchSize: number;
    maxConcurrent: number;
    launchIntervalMs: number;
  } | null;
  lastRunWasCancelled: boolean;
}

export interface ValidationRule {
  id: string;
  name: string;
  description: string;
  validate: (file: File) => Promise<{
    isValid: boolean;
    message?: string;
    errors?: string[];
    warnings?: string[];
  }>;
}

export interface BulkUploadState {
  files: FileWithValidation[];
  isUploading: boolean;
  uploadProgress: number;
  validationRules: ValidationRule[];
  selectedValidationRules: string[];
}

// Server-side validation interfaces
// Import FileMetadata and ServerValidationResult from API instead of duplicating

// Table-specific types
export type FileStatus =
  | 'ready-to-check'
  | 'duplicated'
  | 'allowed'
  | 'not-allowed'
  | 'exists'
  | 'pending'
  | 'uploading'
  | 'uploaded'
  | 'failed';

export enum TableMode {
  DUPLICATE_CHECK = 'duplicate-check',
  VALIDATION = 'validation',
  UPLOAD_PROGRESS = 'upload-progress',
}

export interface ProcessedFile extends FileWithValidation {
  processedStatus: FileStatus;
  duplicateGroup?: string;
}

// Statistics interface
export interface FileStatistics {
  total: number;
  duplicated: number;
  readyToCheck: number;
  serverValidating: number;
  allowed: number;
  notAllowed: number;
  exists: number;
  readyToUpload: number;
  // Upload progress stats
  pending: number;
  uploading: number;
  uploaded: number;
  failed: number;
  // Additional properties for toolbar
  duplicateFileNames?: string[];
  duplicateFileStats?: Record<string, number>;
  hasDuplicates?: boolean;
  hasErrors?: boolean;
  hasValidationResults?: boolean;
}

// Hook return types
export interface UseFileValidationReturn {
  files: FileWithValidation[];
  addFiles: (newFiles: File[] | FileList) => void;
  isAddingFiles: boolean;
  addingFilesProgress: { scanned: number; total: number } | null;
  removeFile: (file: FileWithValidation) => void;
  retryValidation: (file: FileWithValidation) => void;
  clearAllFiles: () => void;
  validateFilesMeta: () => Promise<void>;
  metaValidationProgress: MetaValidationProgress;
  cancelMetaValidation: () => void;
  pauseMetaValidation: () => void;
  resumeMetaValidation: () => void;
  retryFailedMetaValidation: () => Promise<void>;
  setUploadStrategy: (
    file: FileWithValidation,
    strategy: FileUploadStrategy
  ) => void;
  getFilesReadyForUpload: () => FileWithValidation[];
}

export interface UseFileStatisticsReturn {
  statistics: FileStatistics;
  duplicateFileNames: string[];
  duplicateFileStats: Record<string, number>;
  hasErrors: boolean;
  hasValidationResults: boolean;
  hasDuplicates: boolean;
  conflictingFiles: FileWithValidation[];
}

export interface UseUploadHandlersReturn {
  // Refs for JSX
  folderInputRef: React.RefObject<HTMLInputElement | null>;
  filesInputRef: React.RefObject<HTMLInputElement | null>;
  folderInputRefActions: React.RefObject<HTMLInputElement | null>;
  filesInputRefActions: React.RefObject<HTMLInputElement | null>;

  // Handlers
  handleSelectFolder: () => void;
  handleSelectFiles: () => void;
  handleFolderChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddFolder: () => void;
  handleAddMoreFiles: () => void;
  handleFolderChangeActions: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleFilesChangeActions: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleRemoveFile: (file: FileWithValidation) => void;
  handleRetryValidation: (file: FileWithValidation) => void;
  handleClearAllFiles: () => void;
  handleValidateAll: () => void;
  handleStartValidation: () => Promise<void>;
  handleUploadFiles: () => void;
  handleConfirmUpload: () => void;
}

// Progress tracking types
export type ValidationStep =
  | 'file-selection'
  | 'server-validation'
  | 'ready-to-upload'
  | 'upload-complete';
export type StepStatus =
  | 'pending'
  | 'current'
  | 'processing'
  | 'completed'
  | 'error';

// Upload statistics interface
export interface UploadStatistics {
  filesToUpload: number;
  newFiles: number;
  replacedFiles: number;
  skippedNotApproved: number;
  skippedDuplicates: number;
  skippedAlreadyExist: number;
}
